/**
 * The durations of a remote support session (Fernwartung): the steps the
 * admin may grant — master takes 15 to 480 minutes and never more than the
 * operator asked for (`support.MinMinutes`, `MaxMinutes`) — and their words.
 */
import { formatDuration } from '@blpde/nx-ui';

/** The steps an approval offers, in minutes (`edgeproto.MinSupportMinutes` … `MaxSupportMinutes`). */
export const SUPPORT_MINUTE_STEPS = [15, 30, 60, 120, 240, 480] as const;

/** The most a request may ask for and an approval may grant, in minutes. */
export const MAX_SUPPORT_MINUTES = 480;

/**
 * What an admin may approve for a request of `requested` minutes: the asked
 * duration and every shorter step. Never more than was asked.
 */
export function approvableMinutes(requested: number): number[] {
  const asked = Math.min(
    Math.max(Math.round(requested) || 0, SUPPORT_MINUTE_STEPS[0]),
    MAX_SUPPORT_MINUTES
  );
  const steps: number[] = SUPPORT_MINUTE_STEPS.filter((step) => step <= asked);
  if (!steps.includes(asked)) steps.push(asked);
  return steps.sort((a, b) => a - b);
}

type Translate = (key: string, named?: Record<string, unknown>, plural?: number) => string;

/** „45 Minuten“, „1 Stunde“, „2 Stunden“; anything in between as „1 Std. 30 Min.“. */
export function supportDurationLabel(minutes: number, t: Translate, locale?: string): string {
  if (minutes < 60) return t('connect.support.duration.minutes', { n: minutes });
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return t('connect.support.duration.hours', { n: hours }, hours);
  }
  return formatDuration(minutes, { style: 'long', locale });
}
